import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';

import type {
  DepartmentResponse,
  GetDepartmentListParams,
  GetDepartmentListResponse,
} from '@/types/department';
import { getDepartmentById, getDepartmentList } from '@/api/department';
import { useAuth } from '@/context/AuthContext';
import { mergeQueryOptions } from '@/utility/queryOptions';
import { QueryKeys } from '@gokrew/models/constants';
import { fetchAllPages } from '@gokrew/ui/utility/fetchAllPages';

export function useDepartment(
  params: GetDepartmentListParams,
  options: Omit<
    UseQueryOptions<GetDepartmentListResponse>,
    'queryKey' | 'queryFn'
  > = {}
) {
  const { companyId, branchId: authBranchId } = useAuth();
  const effectiveBranchId = params.branchId ?? authBranchId;
  return useQuery<GetDepartmentListResponse>({
    queryKey: [QueryKeys.Departments, params, effectiveBranchId],
    queryFn: () =>
      getDepartmentList(companyId!, {
        ...params,
        ...(effectiveBranchId ? { branchId: effectiveBranchId } : {}),
      }),
    placeholderData: keepPreviousData,
    ...mergeQueryOptions(options, [!!companyId]),
  });
}

export function useDepartmentById(
  id: string,
  options: Omit<
    UseQueryOptions<DepartmentResponse>,
    'queryKey' | 'queryFn'
  > = {}
) {
  const { companyId } = useAuth();
  return useQuery<DepartmentResponse>({
    queryKey: [QueryKeys.Department, id],
    queryFn: () => getDepartmentById(companyId!, id),
    placeholderData: keepPreviousData,
    ...mergeQueryOptions(options, [!!companyId, !!id]),
  });
}

export const useInfiniteDepartments = (
  params: Omit<GetDepartmentListParams, 'page'>
) => {
  const { companyId, branchId: authBranchId } = useAuth();
  const effectiveBranchId = params.branchId ?? authBranchId;
  return useInfiniteQuery({
    queryKey: [QueryKeys.Departments, 'infinite', params, effectiveBranchId],
    queryFn: ({ pageParam }) =>
      getDepartmentList(companyId!, {
        ...params,
        page: pageParam,
        ...(effectiveBranchId ? { branchId: effectiveBranchId } : {}),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.data.meta.hasNextPage ? lastPage.data.meta.page + 1 : undefined,
    enabled: !!companyId,
  });
};

/**
 * Every department (all pages), for screens that list or name them all:
 * a company can have more than one page of them.
 */
export function useAllDepartments(
  params: Omit<GetDepartmentListParams, 'page' | 'limit'> = {},
  options: { enabled?: boolean } = {}
) {
  const { companyId, branchId: authBranchId } = useAuth();
  const effectiveBranchId = params.branchId ?? authBranchId;
  return useQuery<GetDepartmentListResponse>({
    queryKey: [QueryKeys.Departments, 'all', params, effectiveBranchId],
    queryFn: () =>
      fetchAllPages((page, limit) =>
        getDepartmentList(companyId!, {
          ...params,
          page,
          limit,
          ...(effectiveBranchId ? { branchId: effectiveBranchId } : {}),
        })
      ),
    enabled: !!companyId && (options.enabled ?? true),
  });
}
